import { useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { confirmAction, cancelAction, getActionStatus } from "@/lib/aiActions";
import ConfirmActionDialog from "./ConfirmActionDialog";
import ActionResultChip from "./ActionResultChip";

/**
 * Phase 4 · In-chat / in-panel card showing an AI-proposed action
 * awaiting the user's approval.
 *
 * Props:
 *   proposal: {
 *     action_row_id, action_id, policy, confirmation_token,
 *     expires_at, payload, description, sensitivity
 *   }
 *   onDone?: (newState) => void
 */
export default function ProposedActionCard({ proposal, originWorkspace, originActor, initialState, onDone }) {
  const [state, setState] = useState(() =>
    ["succeeded", "failed", "cancelled", "unknown", "expired"].includes(initialState)
      ? initialState : "awaiting_confirmation");

  const [resultMsg, setResultMsg] = useState(() =>
    initialState === "unknown"
      ? "The action outcome is uncertain. Check its stored status before attempting anything again."
      : initialState === "expired" ? "Request a new proposal before running this action." : "");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const { user, activeWorkspace } = useAuth();
  // Preserve the request origin; mounting a card must not assign a new company.
  const proposalWorkspace = useRef(originWorkspace);
  const proposalActor = useRef(originActor);
  const actionPending = useRef(false);

  if (!proposal) return null;

  const {
    action_row_id,
    action_id,
    confirmation_token,
    expires_at,
    payload,
    description,
    sensitivity,
  } = proposal;

  const sensColor =
    sensitivity === "high"
      ? "text-red-700 border-red-200 bg-red-50"
      : sensitivity === "medium"
      ? "text-amber-700 border-amber-200 bg-amber-50"
      : "text-neutral-600 border-neutral-200 bg-neutral-50";

  const handleApprove = async () => {
    if (actionPending.current || state !== "awaiting_confirmation") return;
    if (!proposalWorkspace.current || !proposalActor.current ||
        proposalActor.current !== user?.id || proposalWorkspace.current !== (activeWorkspace || user?.id)) {
      toast.error("The active workspace changed. Return to the original workspace before confirming this action.");
      return;
    }
    const expiresAt = Date.parse(expires_at);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      setState("expired");
      setResultMsg("This proposal has expired or its validity could not be confirmed. Request a new proposal before running the action.");
      setDialogOpen(false);
      onDone && onDone("expired");
      return;
    }
    actionPending.current = true;
    setState("executing");
    try {
      const data = await confirmAction(action_row_id, confirmation_token, proposalWorkspace.current);
      if (data?.state !== "succeeded") {
        throw new Error("Action completion was not confirmed.");
      }
      setState("succeeded");
      setResultMsg("");
      toast.success("Action completed");
      setDialogOpen(false);
      onDone && onDone("succeeded");
    } catch (e) {
      // A missing/failed response does not establish whether execution occurred.
      setState("unknown");
      setResultMsg("The outcome could not be confirmed. Check the affected record before attempting this action again.");
      toast.error("Action outcome is uncertain. Check the affected record before retrying.");
      setDialogOpen(false);
      onDone && onDone("unknown");
    } finally {
      actionPending.current = false;
    }
  };

  const handleCancel = async () => {
    if (actionPending.current || state !== "awaiting_confirmation") return;
    if (!proposalWorkspace.current || !proposalActor.current ||
        proposalActor.current !== user?.id || proposalWorkspace.current !== (activeWorkspace || user?.id)) {
      toast.error("The active workspace changed. Return to the original workspace before cancelling this action.");
      return;
    }
    actionPending.current = true;
    try {
      const data = await cancelAction(action_row_id, proposalWorkspace.current);
      if (data?.state !== "cancelled") throw new Error("Cancellation was not confirmed.");
      setState("cancelled");
      toast.success("Action cancelled");
      onDone && onDone("cancelled");
    } catch (e) {
      setState("unknown");
      setResultMsg("Cancellation could not be confirmed. Check the action status before trying again.");
      toast.error("Cancellation could not be confirmed.");
      onDone && onDone("unknown");
    } finally {
      actionPending.current = false;
    }
  };

  const checkStatus = async () => {
    if (actionPending.current) return;
    if (!proposalWorkspace.current || !proposalActor.current ||
        proposalActor.current !== user?.id || proposalWorkspace.current !== (activeWorkspace || user?.id)) {
      toast.error("Return to the original workspace to check this action.");
      return;
    }
    actionPending.current = true;
    setChecking(true);
    try {
      const data = await getActionStatus(action_row_id, proposalWorkspace.current);
      if (["succeeded", "failed", "cancelled"].includes(data?.state)) {
        setState(data.state);
        setResultMsg("");
        onDone && onDone(data.state);
      } else {
        setResultMsg("No final outcome is recorded yet. Check again later; this does not rerun the action.");
      }
    } catch {
      setResultMsg("The status could not be retrieved. Check the affected record before attempting the action again.");
    } finally {
      actionPending.current = false;
      setChecking(false);
    }
  };

  return (
    <div
      data-testid="ai-proposed-action-card"
      className="mt-2 rounded-lg border border-[#202B60]/20 bg-white p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#202B60]" />
          <div className="text-sm font-medium text-neutral-900">
            Proposed action
          </div>
          <span
            data-testid="ai-proposed-action-sensitivity"
            className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-md border ${sensColor} uppercase tracking-wide`}
          >
            {sensitivity}
          </span>
        </div>
        {state !== "awaiting_confirmation" && (
          <ActionResultChip state={state} message={resultMsg} />
        )}
      </div>
      <div className="text-sm text-neutral-700">{description}</div>
      <div
        data-testid="ai-proposed-action-id"
        className="font-mono text-[11px] text-neutral-500"
      >
        {action_id}
      </div>
      <details className="text-xs text-neutral-600">
        <summary className="cursor-pointer select-none">
          Material parameters
        </summary>
        <pre className="mt-1 bg-neutral-50 border border-neutral-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all">
          {JSON.stringify(payload || {}, null, 2)}
        </pre>
      </details>
      {state === "awaiting_confirmation" && (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            data-testid="ai-proposed-action-approve"
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#202B60] hover:bg-[#182149] px-3 py-1.5 rounded-md transition-colors"
          >
            Approve & run
          </button>
          <button
            type="button"
            data-testid="ai-proposed-action-cancel"
            onClick={handleCancel}
            className="inline-flex items-center gap-1 text-xs font-medium text-neutral-700 border border-neutral-300 hover:border-neutral-500 px-3 py-1.5 rounded-md bg-white transition-colors"
          >
            Cancel
          </button>
          {expires_at && (
            <span className="text-[10px] text-neutral-500">
              expires {new Date(expires_at).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
      {state === "expired" && <p role="alert" className="text-xs text-amber-800">{resultMsg}</p>}
      {state === "unknown" && <div className="text-xs text-amber-800">
        <p>{resultMsg}</p>
        <button type="button" disabled={checking} onClick={checkStatus}
          className="mt-1 underline" data-testid="ai-action-check-status">
          {checking ? "Checking status…" : "Check status without rerunning"}
        </button>
      </div>}
      <ConfirmActionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        actionId={action_id}
        description={description}
        payload={payload}
        onConfirm={handleApprove}
      />
    </div>
  );
}
